import React, { useState, FC, useEffect } from "react";
import "./CustomDatePicker.css";
import WeekView from "./WeekView";
import dayjs from "dayjs";
import "./CustomDatePicker.css";
import clsx from "clsx";
import ChevronRight from "@spectrum-icons/workflow/ChevronRight";
import ChevronLeft from "@spectrum-icons/workflow/ChevronLeft";

interface CustomDatePickerProps {
  onDateSelect?: (date: dayjs.Dayjs) => void;
  selectedDate?: dayjs.Dayjs;
  width?: string;
}

const CustomDatePicker: FC<CustomDatePickerProps> = ({ onDateSelect, selectedDate, width = "100%" }) => {
  const [initialDate, setInitialDate] = useState<dayjs.Dayjs>(() =>
    selectedDate || dayjs()
  );

  // Helper function to get next N working days
  const getNextWorkingDays = (startDate: dayjs.Dayjs, count: number): dayjs.Dayjs[] => {
    const workingDays: dayjs.Dayjs[] = [];
    let currentDate = startDate;
    
    while (workingDays.length < count) {
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (currentDate.day() !== 0 && currentDate.day() !== 6) {
        workingDays.push(currentDate);
      }
      currentDate = currentDate.add(1, 'day');
    }
    
    return workingDays;
  };

  // Get the allowed date range (next 5 working days)
  const getAllowedDateRange = () => {
    const today = dayjs().startOf('day');
    
    // If today is Saturday or Sunday, start from next Monday
    let startDate = today;
    if (today.day() === 0) { // Sunday
      startDate = today.add(1, 'day'); // Monday
    } else if (today.day() === 6) { // Saturday
      startDate = today.add(2, 'day'); // Monday
    }
    
    const allowedDates = getNextWorkingDays(startDate, 5);
    return {
      start: allowedDates[0],
      end: allowedDates[allowedDates.length - 1]
    };
  };

  // Effect to set today's date as default if no selectedDate is provided
  useEffect(() => {
    if (!selectedDate && onDateSelect) {
      const today = dayjs().startOf('day');
      const { start: minAllowedDate, end: maxAllowedDate } = getAllowedDateRange();
      
      // Check if today is within the allowed range
      if ((today.isAfter(minAllowedDate) || today.isSame(minAllowedDate)) && 
          (today.isBefore(maxAllowedDate) || today.isSame(maxAllowedDate))) {
        // If today is a working day, select it
        if (today.day() !== 0 && today.day() !== 6) {
          onDateSelect(today);
        } else {
          // If today is weekend, select the next working day
          const nextWorkingDay = getNextWorkingDays(today, 1)[0];
          if (nextWorkingDay && (nextWorkingDay.isBefore(maxAllowedDate) || nextWorkingDay.isSame(maxAllowedDate))) {
            onDateSelect(nextWorkingDay);
          }
        }
      } else {
        // If today is not in allowed range, select the first allowed date
        onDateSelect(minAllowedDate);
      }
    }
  }, [selectedDate, onDateSelect]);

  const forth = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { end: maxAllowedDate } = getAllowedDateRange();
    const nextWeekStart = initialDate.add(7, "day").day(0);
    
    // Only allow going forward if the next week contains allowed dates
    if (nextWeekStart.isBefore(maxAllowedDate) || nextWeekStart.isSame(maxAllowedDate, 'week')) {
      setInitialDate(initialDate.add(7, "day"));
    }
  };

  const back = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { start: minAllowedDate } = getAllowedDateRange();
    const previousWeekStart = initialDate.subtract(7, "day").day(0);
    
    // Only allow going back if the previous week contains allowed dates
    if (previousWeekStart.isAfter(minAllowedDate) || previousWeekStart.isSame(minAllowedDate, 'week')) {
      setInitialDate(initialDate.subtract(7, "day"));
    }
  };

  // Check if we can go back
  const canGoBack = () => {
    const { start: minAllowedDate } = getAllowedDateRange();
    const previousWeekStart = initialDate.subtract(7, "day").day(0);
    return previousWeekStart.isAfter(minAllowedDate) || previousWeekStart.isSame(minAllowedDate, 'week');
  };

  // Check if we can go forward
  const canGoForward = () => {
    const { end: maxAllowedDate } = getAllowedDateRange();
    const nextWeekStart = initialDate.add(7, "day").day(0);
    return nextWeekStart.isBefore(maxAllowedDate) || nextWeekStart.isSame(maxAllowedDate, 'week');
  };

  return (
    <div className="cb-datepicker-container" style={{ width }}>
      <div className="cb-button-wrapper">
        <button
          onClick={(e) => back(e)}
          className={clsx("cb-arrow-btn left", {
            ["cb-disabled-dates"]: !canGoBack(),
          })}
          disabled={!canGoBack()}
        >
          <ChevronLeft />
        </button>
      </div>
      <WeekView initialDate={initialDate} onDateSelect={onDateSelect} selectedDate={selectedDate} />
      <div className="cb-button-wrapper">
        <button
          onClick={(e) => forth(e)}
          className={clsx("cb-arrow-btn right", {
            ["cb-disabled-dates"]: !canGoForward(),
          })}
          disabled={!canGoForward()}
        >
         <ChevronRight />
        </button>
      </div>
    </div>
  );
};

export default CustomDatePicker;